import { getFormatLocale, translateLabel } from "@/i18n/messages";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  CrosshairIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PathIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { PlanThisRideButton } from "@/components/PlanThisRideButton";
import { Skeleton } from "@/components/ui/skeleton";
import { api, ACCESS_LABELS, STYLE_LABELS } from "@/lib/apiClient";
import {
  compactDayTicketLabel,
  compareOriginFromSearchParams,
  MAX_COMPARE_SPOTS,
  normalizeCompareIds,
} from "@/lib/compareSpots";
import { destinationCoverImage } from "@/lib/destinationCover";
import { currentDestinationStatusLabel } from "@/lib/destinationDetail";
import { parkPath } from "@/lib/routes";
import { formatDistanceKm } from "@/lib/units";
import {
  precipitationLabel,
  temperatureRangeLabel,
  weatherConditionLabel,
} from "@/lib/weatherPresentation";
import { useUnitPreference } from "@/context/UnitPreferenceContext";
import { trackEvent } from "@/lib/analytics";
import { conciseStatusWarning } from "@/lib/statusTrust";
import { buildCompareDifferenceSummary } from "@/lib/compareDifferenceSummary";

const humanize = (value) => {
  if (!value) return "—";
  const label = String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part ? part[0].toUpperCase() + part.slice(1) : part)
    .join(" ");
  return translateLabel(label);
};

const dateValue = (value) => {
  if (!value) return null;
  const text = String(value);
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(text)
    ? new Date(`${text}T12:00:00`)
    : new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateShort = (value, { weekday = false, year = false } = {}) => {
  const parsed = dateValue(value);
  if (!parsed) return value || "—";
  return new Intl.DateTimeFormat(getFormatLocale(), {
    ...(weekday ? { weekday: "short" } : {}),
    day: "numeric",
    month: "short",
    ...(year ? { year: "numeric" } : {}),
  }).format(parsed);
};

const joinedLabels = (values, labels) => {
  if (!Array.isArray(values) || values.length === 0) return "—";
  return values.map((value) => translateLabel(labels[value] || humanize(value))).join(" · ");
};

const safeReturnPath = (value) =>
  value?.startsWith("/") && !value.startsWith("//") ? value : "/";

const legalAccessLabel = (park) => {
  const legal = park.visit_info?.legal_access;
  if (!legal) return "Unknown";
  const base = humanize(legal.status);
  const details = [
    legal.requires_ticket ? translateLabel("ticket required") : null,
    legal.requires_waiver ? translateLabel("waiver required") : null,
  ].filter(Boolean);
  return details.length ? `${base} · ${details.join(" · ")}` : base;
};

const upliftLabel = (park) => {
  const uphill = park.visit_info?.uphill_access;
  if (!uphill) return joinedLabels(park.access, ACCESS_LABELS);
  const parts = [];
  if (uphill.uplift_kind && uphill.uplift_kind !== "UNKNOWN" && uphill.uplift_kind !== "NONE") {
    parts.push(humanize(uphill.uplift_kind));
  }
  if (uphill.pedal_access === "YES") parts.push(translateLabel("Pedal"));
  if (uphill.service_name) parts.push(uphill.service_name);
  return parts.length ? parts.join(" · ") : "—";
};

const transportLabel = (park) => {
  const uphill = park.visit_info?.uphill_access;
  if (!uphill) return "—";
  const bike = humanize(uphill.bike_transport);
  const ebike = humanize(uphill.e_bike_transport);
  return `${translateLabel("Bike")}: ${bike} · E-bike: ${ebike}`;
};

const weatherLabel = (weather) => {
  if (!weather) return translateLabel("Unavailable");
  return [
    weatherConditionLabel(weather.condition),
    temperatureRangeLabel(weather),
    precipitationLabel(weather),
  ].join(" · ");
};

const DIFFICULTY_ITEMS = [
  { key: "easy", label: "Easy", dot: "bg-emerald-400" },
  { key: "intermediate", label: "Intermediate", dot: "bg-blue-400" },
  { key: "advanced", label: "Advanced", dot: "bg-red-500" },
  { key: "expert", label: "Expert", dot: "bg-zinc-100" },
];

const CompactDifficulty = ({ distribution = {} }) => {
  const available = DIFFICULTY_ITEMS
    .map((item) => ({ ...item, count: Number(distribution[item.key] || 0) }))
    .filter((item) => item.count > 0);

  if (!available.length) return "—";

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      {available.map((item) => (
        <span
          key={item.key}
          title={`${translateLabel(item.label)} — ${item.count} trail${item.count === 1 ? "" : "s"}`}
          className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-zinc-200"
        >
          <span className={`h-2.5 w-2.5 rounded-full ring-1 ring-white/15 ${item.dot}`} aria-hidden="true" />
          {item.count}
        </span>
      ))}
    </div>
  );
};

const StatusValue = ({ park }) => {
  const status = park.current_status || "UNKNOWN";
  const tone = status === "OPEN_NOW"
    ? "bg-emerald-400"
    : status === "CLOSED_NOW"
      ? "bg-red-500"
      : status === "RESTRICTED"
        ? "bg-amber-400"
        : "bg-zinc-500";
  const warning = conciseStatusWarning(park.operational_status, park.operational_status_verification);

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <span
        title={park.current_status_reason || undefined}
        className="inline-flex items-center gap-2"
      >
        <span className={`h-2.5 w-2.5 rounded-full ${tone}`} aria-hidden="true" />
        {currentDestinationStatusLabel(status)}
      </span>
      {warning && (
        <span className="text-[11px] font-semibold text-amber-300" data-testid="compare-status-warning">
          ⚠ {warning}
        </span>
      )}
    </span>
  );
};

const CompareCell = ({ children, muted = false }) => (
  <div className={`min-w-0 border-l border-white/[0.06] px-3 py-3 text-sm ${muted ? "text-zinc-600" : "text-zinc-200"} sm:px-4`}>
    {children}
  </div>
);


const MobileSpotHeader = ({ item, role, onMakeReference, planDate, planOrigin }) => {
  if (!item) return null;
  const { park } = item;
  const compactPrice = compactDayTicketLabel(park.day_ticket_price);
  return (
    <div className="min-w-0 rounded-2xl border border-white/[0.08] bg-black/20 p-3">
      <div className="text-[9px] font-black uppercase tracking-[0.16em] text-zinc-500">{role}</div>
      <img src={destinationCoverImage(park)} alt="" className="mt-2 h-20 w-full rounded-xl object-cover" />
      <div className="mt-2 font-display text-lg font-bold leading-tight text-white">{park.name}</div>
      <div className="mt-1 text-[11px] text-zinc-500">
        {[park.city, park.country].filter(Boolean).join(", ") || translateLabel("Location unavailable")}
      </div>
      {compactPrice && (
        <div className="mt-2 inline-flex rounded-full border border-[#CCFF00]/20 bg-[#CCFF00]/10 px-2 py-0.5 text-[10px] font-bold text-[#CCFF00]">
          {compactPrice}
        </div>
      )}
      <div className="mt-3 flex items-center gap-3">
        <Link to={parkPath(park)} className="text-[11px] font-bold text-[#CCFF00] hover:text-white">
          View park →
        </Link>
        <PlanThisRideButton
          destination={park}
          initialDate={planDate}
          initialOrigin={planOrigin}
          source="compare_mobile"
          compact
        />
        {onMakeReference && (
          <button
            type="button"
            onClick={onMakeReference}
            className="text-[11px] font-bold text-zinc-400 hover:text-white"
            data-testid="make-compare-reference"
          >
            Make reference
          </button>
        )}
      </div>
    </div>
  );
};

const browserPosition = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error(translateLabel("Your browser does not support location.")));
    return;
  }
  navigator.geolocation.getCurrentPosition(resolve, reject, {
    enableHighAccuracy: false,
    timeout: 10000,
    maximumAge: 300000,
  });
});

export default function CompareSpots() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { unitSystem } = useUnitPreference();
  const spotIds = useMemo(() => normalizeCompareIds(searchParams.get("spots")), [searchParams]);
  const origin = useMemo(() => compareOriginFromSearchParams(searchParams), [searchParams]);
  const explicitOriginKey = origin ? `${origin.lat}:${origin.lng}:${origin.name}` : "";
  const returnPath = safeReturnPath(searchParams.get("from"));
  const spotKey = spotIds.join(",");
  const requestedDate = searchParams.get("date") || "";
  const selectedDate = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? requestedDate : "";

  const [profileOrigin, setProfileOrigin] = useState(null);
  const [profileOriginChecked, setProfileOriginChecked] = useState(Boolean(origin));
  const effectiveOrigin = origin || profileOrigin;
  const originLat = effectiveOrigin?.lat ?? null;
  const originLng = effectiveOrigin?.lng ?? null;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [missingCount, setMissingCount] = useState(0);
  const [routingAvailable, setRoutingAvailable] = useState(null);
  const loadedOnce = useRef(false);

  const [originEditorOpen, setOriginEditorOpen] = useState(false);
  const [differenceExpanded, setDifferenceExpanded] = useState(false);
  const [addSpotOpen, setAddSpotOpen] = useState(false);
  const [originQuery, setOriginQuery] = useState("");
  const [originBusy, setOriginBusy] = useState(false);

  const [replacementQuery, setReplacementQuery] = useState("");
  const [replacementResults, setReplacementResults] = useState([]);
  const [replacementBusy, setReplacementBusy] = useState(false);
  const [referenceSpotId, setReferenceSpotId] = useState(null);
  const [challengerIndex, setChallengerIndex] = useState(0);

  useEffect(() => {
    if (explicitOriginKey) {
      setProfileOrigin(null);
      setProfileOriginChecked(true);
      return;
    }

    let active = true;
    setProfileOriginChecked(false);
    api.get("/rider-profile")
      .then(({ data }) => {
        if (!active) return;
        const home = data?.home_location;
        setProfileOrigin(
          home && Number.isFinite(home.lat) && Number.isFinite(home.lng)
            ? { lat: home.lat, lng: home.lng, name: home.name || translateLabel("Home location") }
            : null,
        );
      })
      .catch(() => {
        if (active) setProfileOrigin(null);
      })
      .finally(() => {
        if (active) setProfileOriginChecked(true);
      });

    return () => { active = false; };
  }, [explicitOriginKey]);

  useEffect(() => {
    setOriginQuery(effectiveOrigin?.name || "");
  }, [effectiveOrigin?.name]);

  useEffect(() => {
    let cancelled = false;
    if (!origin && !profileOriginChecked) return () => { cancelled = true; };

    if (!spotKey) {
      setItems([]);
      setRoutingAvailable(null);
      setLoading(false);
      loadedOnce.current = true;
      return () => { cancelled = true; };
    }

    if (!loadedOnce.current) setLoading(true);
    setMissingCount(0);

    const ids = normalizeCompareIds(spotKey);
    const requestOrigin = originLat != null && originLng != null
      ? { lat: originLat, lng: originLng }
      : null;

    const parkRequests = Promise.allSettled(ids.map(async (id) => {
      const parkResult = await api.get(`/parks/${id}`, {
        params: requestOrigin || {},
      });

      let weather = null;
      if (!selectedDate) {
        try {
          const weatherResult = await api.get(`/parks/${id}/weather`);
          weather = weatherResult.data?.days?.[0] || null;
        } catch {
          weather = null;
        }
      }

      return { park: parkResult.data, weather };
    }));

    const contextRequest = api.get("/compare-context", {
      params: {
        destination_ids: ids,
        ...(requestOrigin || {}),
        date: selectedDate || undefined,
      },
    }).catch(() => ({ data: null }));

    Promise.all([parkRequests, contextRequest]).then(([results, contextResult]) => {
      if (cancelled) return;
      const compareContext = contextResult.data;
      const contextById = new Map(
        (compareContext?.items || []).map((item) => [item.destination_id, item]),
      );

      const loaded = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => {
          const value = result.value;
          const contextItem = contextById.get(value.park.id);
          return {
            ...value,
            route: contextItem
              ? { drive_km: contextItem.drive_km, drive_minutes: contextItem.drive_minutes }
              : null,
            weather: selectedDate ? contextItem?.weather || null : value.weather,
          };
        });

      setItems(loaded);
      setMissingCount(results.length - loaded.length);
      setRoutingAvailable(requestOrigin ? Boolean(compareContext?.routing_available) : null);
      setLoading(false);
      loadedOnce.current = true;
      trackEvent("compare_spots_view", {
        spot_count: loaded.length,
        has_origin: Boolean(requestOrigin),
        has_date: Boolean(selectedDate),
        routing_available: requestOrigin ? Boolean(compareContext?.routing_available) : null,
      });
    });

    return () => { cancelled = true; };
  }, [spotKey, originLat, originLng, origin, profileOriginChecked, selectedDate]);

  useEffect(() => {
    const query = replacementQuery.trim();
    if (spotIds.length >= MAX_COMPARE_SPOTS || query.length < 2) {
      setReplacementResults([]);
      setReplacementBusy(false);
      return undefined;
    }

    let active = true;
    setReplacementBusy(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: query } });
        if (!active) return;
        setReplacementResults(
          (data?.destinations || [])
            .filter((candidate) => !spotIds.includes(candidate.id))
            .slice(0, 5),
        );
      } catch {
        if (active) setReplacementResults([]);
      } finally {
        if (active) setReplacementBusy(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [replacementQuery, spotKey, spotIds]);

  const updateOrigin = (location) => {
    const next = new URLSearchParams(searchParams);
    next.set("lat", String(location.lat));
    next.set("lng", String(location.lng));
    next.set("place", location.name || "Selected location");
    setSearchParams(next, { replace: true });
    setOriginEditorOpen(false);
  };

  const updateDate = (value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set("date", value);
    else next.delete("date");
    setSearchParams(next, { replace: true });
  };

  const searchOrigin = async () => {
    const query = originQuery.trim();
    if (!query) return;
    setOriginBusy(true);
    try {
      const { data } = await api.get("/geocode", { params: { q: query } });
      const first = data?.results?.[0];
      if (!first) return;
      updateOrigin({ name: first.name, lat: first.lat, lng: first.lng });
    } finally {
      setOriginBusy(false);
    }
  };

  const useCurrentOrigin = async () => {
    setOriginBusy(true);
    try {
      const position = await browserPosition();
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      let name = "Current location";
      try {
        const { data } = await api.get("/reverse-geocode", { params: { lat, lng } });
        if (data?.name) name = data.name;
      } catch {
        // Coordinates remain usable even if reverse geocoding fails.
      }
      updateOrigin({ name, lat, lng });
    } finally {
      setOriginBusy(false);
    }
  };

  const removeSpot = (id) => {
    setItems((current) => current.filter((item) => item.park.id !== id));
    const next = spotIds.filter((spotId) => spotId !== id);
    const nextParams = new URLSearchParams(searchParams);
    if (next.length) nextParams.set("spots", next.join(","));
    else nextParams.delete("spots");
    setSearchParams(nextParams, { replace: true });
    setReplacementQuery("");
    setReplacementResults([]);
    trackEvent("compare_spot_removed", { park_id: String(id), remaining_count: next.length });
  };

  const addSpot = (id) => {
    const next = normalizeCompareIds([...spotIds, id]);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("spots", next.join(","));
    setSearchParams(nextParams, { replace: true });
    setReplacementQuery("");
    setReplacementResults([]);
    trackEvent("compare_spot_added", { park_id: String(id), source: "compare" });
  };

  const itemById = useMemo(
    () => new Map(items.map((item) => [item.park.id, item])),
    [items],
  );

  const loadedIds = useMemo(() => items.map((item) => item.park.id), [items]);

  useEffect(() => {
    if (!loadedIds.length) {
      setReferenceSpotId(null);
      setChallengerIndex(0);
      return;
    }
    if (!referenceSpotId || !loadedIds.includes(referenceSpotId)) {
      setReferenceSpotId(loadedIds[0]);
      setChallengerIndex(0);
    }
  }, [loadedIds, referenceSpotId]);

  const referenceItem = referenceSpotId ? itemById.get(referenceSpotId) || null : null;
  const challengerItems = items.filter((item) => item.park.id !== referenceSpotId);
  const safeChallengerIndex = challengerItems.length
    ? Math.min(challengerIndex, challengerItems.length - 1)
    : 0;
  const challengerItem = challengerItems[safeChallengerIndex] || null;

  useEffect(() => {
    if (challengerIndex !== safeChallengerIndex) setChallengerIndex(safeChallengerIndex);
  }, [challengerIndex, safeChallengerIndex]);

  const makeReference = (id) => {
    setReferenceSpotId(id);
    setChallengerIndex(0);
    trackEvent("compare_reference_changed", { park_id: String(id) });
  };

  const slots = spotIds.map((id) => ({ id, item: itemById.get(id) || null }));

  const rows = [
    {
      label: "Status",
      render: ({ park }) => <StatusValue park={park} />,
    },
    effectiveOrigin ? {
      label: "Drive",
      render: ({ route }) => {
        if (!routingAvailable || route?.drive_minutes == null) return translateLabel("Unavailable");
        const routedDistance = route.drive_km != null ? formatDistanceKm(route.drive_km, unitSystem) : null;
        return `${route.drive_minutes} min${routedDistance ? ` · ${routedDistance}` : ""}`;
      },
    } : null,
    {
      label: "Trails",
      render: ({ park }) => translateLabel("{count} trails", { count: park.trail_count ?? 0 }),
    },
    {
      label: "Difficulty",
      render: ({ park }) => <CompactDifficulty distribution={park.difficulty_distribution || {}} />,
    },
    {
      label: "Riding styles",
      render: ({ park }) => joinedLabels(park.riding_styles, STYLE_LABELS),
    },
    {
      label: "Access",
      render: ({ park }) => joinedLabels(park.access, ACCESS_LABELS),
    },
    {
      label: "Legal access",
      render: ({ park }) => legalAccessLabel(park),
    },
    {
      label: "Uplift",
      render: ({ park }) => upliftLabel(park),
    },
    {
      label: "Bike transport",
      render: ({ park }) => transportLabel(park),
    },
    {
      label: selectedDate ? `${translateLabel("Weather")} · ${formatDateShort(selectedDate, { weekday: true })}` : translateLabel("Weather today"),
      render: ({ weather }) => weatherLabel(weather),
    },
    {
      label: "Rating",
      render: ({ park }) => park.rating_count > 0
        ? `${Number(park.rating).toFixed(1)} · ${translateLabel(park.rating_count === 1 ? "{count} rating" : "{count} ratings", { count: park.rating_count })}`
        : translateLabel("No ratings yet")
    },
    {
      label: "Visit info",
      render: ({ park }) => park.visit_info?.last_verified_at
        ? translateLabel("Verified {date}", { date: formatDateShort(park.visit_info.last_verified_at, { year: true }) })
        : translateLabel("Verification date unavailable")
    },
  ].filter(Boolean);

  const differenceSummary = useMemo(
    () => buildCompareDifferenceSummary(items, { routingAvailable, selectedDate }),
    [items, routingAvailable, selectedDate],
  );

  const columnStyle = {
    "--compare-spot-count": Math.max(spotIds.length, 2),
  };

  return (
    <div className="min-h-[calc(100dvh-var(--app-footer-height))] bg-[#09090b] text-white">
      <Navbar scrollsWithPage brandVariant="mark" />
      <main className="mx-auto max-w-[1600px] px-4 pb-16 pt-24 sm:px-6 lg:px-8 md:pt-28">
        <Link
          to={returnPath}
          className="inline-flex items-center gap-2 text-sm font-bold text-zinc-400 transition-colors hover:text-[#CCFF00]"
        >
          <ArrowLeftIcon size={16} weight="bold" /> {translateLabel(translateLabel("Back to discovery"))}
        </Link>

        <div className="mt-5 max-w-3xl">
          <div className="label-tiny text-[#CCFF00]">{translateLabel(translateLabel("Decision tool"))}</div>
          <h1 className="mt-1 font-display text-4xl font-black uppercase sm:text-5xl">{translateLabel(translateLabel("Compare spots"))}</h1>
          <p className="mt-3 text-sm leading-6 text-zinc-400 sm:text-base">
            Compare the facts that matter for a ride without opening every destination in a separate tab.
          </p>
        </div>

        {profileOriginChecked && (
          <section className="mt-5 rounded-2xl border border-white/[0.08] bg-[#111113] px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#CCFF00]/10 text-[#CCFF00]">
                  <MapPinIcon size={17} weight="bold" />
                </span>
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel(translateLabel("Distance from"))}</div>
                  <div className="truncate text-sm font-bold text-white">
                    {effectiveOrigin?.name || translateLabel("No starting point set")}
                  </div>
                </div>
              </div>

              {effectiveOrigin && !originEditorOpen ? (
                <button
                  type="button"
                  onClick={() => setOriginEditorOpen(true)}
                  className="self-start rounded-full border border-white/10 px-4 py-2 text-xs font-bold text-zinc-300 transition-colors hover:border-white/20 hover:text-white lg:self-auto"
                >
                  Change starting point
                </button>
              ) : (
                <div className="flex w-full flex-col gap-2 sm:flex-row lg:max-w-2xl">
                  <div className="relative min-w-0 flex-1">
                    <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      value={originQuery}
                      onChange={(event) => setOriginQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          searchOrigin();
                        }
                      }}
                      placeholder={translateLabel(translateLabel("City or postcode"))}
                      className="w-full rounded-xl border border-white/10 bg-black/25 py-2.5 pl-9 pr-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/40"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={searchOrigin}
                    disabled={originBusy || !originQuery.trim()}
                    className="rounded-xl bg-[#CCFF00] px-4 py-2.5 text-xs font-extrabold text-black disabled:opacity-45"
                  >
                    Set
                  </button>
                  <button
                    type="button"
                    onClick={useCurrentOrigin}
                    disabled={originBusy}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-xs font-bold text-zinc-300 hover:bg-white/[0.04] disabled:opacity-45"
                  >
                    <CrosshairIcon size={15} weight="bold" /> My location
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="mt-3 rounded-2xl border border-white/[0.08] bg-[#111113] px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-zinc-500">{translateLabel(translateLabel("Ride day"))}</div>
              <div className="mt-1 text-sm font-bold text-white">
                {selectedDate ? formatDateShort(selectedDate, { weekday: true, year: true }) : "Today"}
              </div>
              <p className="mt-1 text-[11px] text-zinc-500">
                Choose a date to compare the same forecast day across every park.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={selectedDate}
                onChange={(event) => updateDate(event.target.value)}
                onClick={(event) => event.currentTarget.showPicker?.()}
                className="rounded-xl border border-white/10 bg-black/25 px-3 py-2 text-sm text-white outline-none focus:border-[#CCFF00]/40 [color-scheme:dark]"
                data-testid="compare-date"
              />
              {selectedDate && (
                <button
                  type="button"
                  onClick={() => updateDate("")}
                  className="rounded-xl border border-white/10 px-3 py-2 text-xs font-bold text-zinc-300 hover:bg-white/[0.04]"
                >
                  Today
                </button>
              )}
            </div>
          </div>
        </section>

        {effectiveOrigin && routingAvailable === false && (
          <p className="mt-3 rounded-xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-xs font-semibold text-amber-200">
            Drive-time routing is unavailable right now. Straight-line location context remains visible, but no drive estimate is shown.
          </p>
        )}

        {loading ? (
          <div className="mt-8 grid gap-3 md:grid-cols-3">
            {Array.from({ length: Math.max(spotIds.length, 2) }).map((_, index) => (
              <Skeleton key={index} className="h-48 rounded-2xl bg-white/5" />
            ))}
          </div>
        ) : spotIds.length === 0 ? (
          <section className="mt-8 rounded-3xl border border-dashed border-white/10 bg-[#121214] px-5 py-12 text-center">
            <PathIcon size={36} className="mx-auto text-zinc-600" />
            <h2 className="mt-3 font-display text-2xl font-bold">{translateLabel(translateLabel("Choose spots to compare"))}</h2>
            <p className="mt-1 text-sm text-zinc-500">{translateLabel(translateLabel("Go back to Discovery and add two to four destinations."))}</p>
            <Link to={returnPath} className="mt-5 inline-flex rounded-full bg-[#CCFF00] px-5 py-2.5 text-sm font-bold text-black">
              Find spots
            </Link>
          </section>
        ) : (
          <>
            {missingCount > 0 && (
              <p className="mt-6 rounded-xl border border-amber-400/20 bg-amber-400/10 px-4 py-3 text-sm text-amber-200">
                {missingCount} selected spot{missingCount === 1 ? "" : "s"} could not be loaded.
              </p>
            )}

            {(differenceSummary.facts.length > 0 || differenceSummary.warnings.length > 0) && (
              <section
                className="mt-4 rounded-xl border border-white/[0.07] bg-[#111113]"
                data-testid="compare-difference-summary"
              >
                <button
                  type="button"
                  onClick={() => setDifferenceExpanded((value) => !value)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
                  data-testid="compare-differences-toggle"
                  aria-expanded={differenceExpanded}
                >
                  <span className="text-[10px] font-black uppercase tracking-[0.15em] text-[#CCFF00]">
                    Key differences
                  </span>
                  <span className="text-[10px] font-bold text-zinc-500">
                    {differenceExpanded
                      ? "Hide"
                      : `${differenceSummary.facts.length} fact${differenceSummary.facts.length === 1 ? "" : "s"}${differenceSummary.warnings.length ? ` · ${differenceSummary.warnings.length} warning${differenceSummary.warnings.length === 1 ? "" : "s"}` : ""} ▾`}
                  </span>
                </button>

                {differenceExpanded && (
                  <div className="border-t border-white/[0.06] px-3 py-3">
                    <div className="grid gap-2 lg:grid-cols-2">
                      {differenceSummary.facts.map((fact) => (
                        <div
                          key={`${fact.kind}:${fact.text}`}
                          className="text-[11px] leading-5 text-zinc-300"
                        >
                          • {fact.text}
                        </div>
                      ))}
                    </div>
                    {differenceSummary.warnings.length > 0 && (
                      <div className="mt-2 space-y-1 text-[10px] leading-4 text-amber-200">
                        {differenceSummary.warnings.map((warning) => <div key={warning}>⚠ {warning}</div>)}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {referenceItem && challengerItem && (
              <div className="mt-6 md:hidden" data-testid="mobile-reference-compare">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="label-tiny text-[#CCFF00]">{translateLabel(translateLabel("Mobile compare"))}</div>
                    <div className="mt-1 text-xs text-zinc-500">
                      Keep one park fixed and compare it against the others.
                    </div>
                  </div>
                  {challengerItems.length > 1 && (
                    <div className="flex shrink-0 items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setChallengerIndex((safeChallengerIndex - 1 + challengerItems.length) % challengerItems.length)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-zinc-300"
                        aria-label={translateLabel(translateLabel("Previous comparison park"))}
                      >
                        ←
                      </button>
                      <span className="min-w-[2.5rem] text-center text-[10px] font-bold text-zinc-500">
                        {safeChallengerIndex + 1}/{challengerItems.length}
                      </span>
                      <button
                        type="button"
                        onClick={() => setChallengerIndex((safeChallengerIndex + 1) % challengerItems.length)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 text-zinc-300"
                        aria-label={translateLabel(translateLabel("Next comparison park"))}
                      >
                        →
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <MobileSpotHeader
                  item={referenceItem}
                  role="Reference"
                  planDate={selectedDate}
                  planOrigin={effectiveOrigin}
                />
                  <MobileSpotHeader
                    item={challengerItem}
                    role="Challenger"
                    onMakeReference={() => makeReference(challengerItem.park.id)}
                    planDate={selectedDate}
                    planOrigin={effectiveOrigin}
                  />
                </div>

                <div className="mt-3 overflow-hidden rounded-2xl border border-white/10 bg-[#121214]">
                  {rows.map((row, index) => (
                    <div
                      key={translateLabel(row.label)}
                      className={index !== rows.length - 1 ? "border-b border-white/[0.06]" : ""}
                    >
                      <div className="px-3 pt-3 text-[10px] font-black uppercase tracking-[0.14em] text-zinc-500">
                        {translateLabel(row.label)}
                      </div>
                      <div className="grid grid-cols-2">
                        <div className="min-w-0 px-3 pb-3 pt-2 text-sm text-zinc-200">
                          {row.render(referenceItem)}
                        </div>
                        <div className="min-w-0 border-l border-white/[0.06] px-3 pb-3 pt-2 text-sm text-zinc-200">
                          {row.render(challengerItem)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {spotIds.length < MAX_COMPARE_SPOTS && (
                  <p className="mt-3 text-[11px] text-zinc-600">
                    {spotIds.length} of {MAX_COMPARE_SPOTS} spots selected. Add another from Discovery or desktop comparison.
                  </p>
                )}
              </div>
            )}

            <div className="mt-6 hidden overflow-x-auto rounded-3xl border border-white/10 bg-[#121214] md:block">
              <div className="min-w-max">
                <div className="compare-grid grid border-b border-white/10" style={columnStyle}>
                  <div className="px-3 py-3 sm:px-4">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="label-tiny">{translateLabel("Compare")}</div>
                        <div className="mt-1 text-xs text-zinc-500">{spotIds.length} of {MAX_COMPARE_SPOTS} spots</div>
                      </div>
                      {spotIds.length < MAX_COMPARE_SPOTS && !addSpotOpen && (
                        <button
                          type="button"
                          onClick={() => setAddSpotOpen(true)}
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-zinc-300 transition-colors hover:border-[#CCFF00]/35 hover:text-[#CCFF00]"
                          aria-label={translateLabel(translateLabel("Add park to comparison"))}
                          title={translateLabel(translateLabel("Add park"))}
                          data-testid="compare-add-spot"
                        >
                          <PlusIcon size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                    {spotIds.length < MAX_COMPARE_SPOTS && addSpotOpen && (
                      <div className="relative mt-2">
                        <MagnifyingGlassIcon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600" />
                        <input
                          value={replacementQuery}
                          onChange={(event) => setReplacementQuery(event.target.value)}
                          onBlur={() => {
                            if (!replacementQuery.trim()) setAddSpotOpen(false);
                          }}
                          autoFocus
                          placeholder={translateLabel(translateLabel("Search park"))}
                          className="w-full rounded-lg border border-white/[0.08] bg-black/25 py-1.5 pl-7 pr-2 text-[11px] text-white outline-none placeholder:text-zinc-600 focus:border-[#CCFF00]/30"
                        />
                        {replacementResults.length > 0 && (
                          <div className="absolute left-0 top-full z-30 mt-1 w-64 overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0d] shadow-2xl">
                            {replacementResults.map((candidate) => (
                              <button
                                key={candidate.id}
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => {
                                  addSpot(candidate.id);
                                  setAddSpotOpen(false);
                                }}
                                className="block w-full border-b border-white/[0.06] px-2.5 py-2 text-left last:border-b-0 hover:bg-white/[0.04]"
                              >
                                <span className="block truncate text-xs font-bold text-white">{candidate.name}</span>
                                <span className="block truncate text-[10px] text-zinc-500">
                                  {[candidate.city, candidate.country].filter(Boolean).join(", ")}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {slots.map((slot, index) => {
                    if (slot?.item) {
                      const { park } = slot.item;
                      const compactPrice = compactDayTicketLabel(park.day_ticket_price);
                      return (
                        <div key={park.id} className="relative min-w-0 border-l border-white/[0.06] p-3">
                          <button
                            type="button"
                            onClick={() => removeSpot(park.id)}
                            aria-label={`Remove ${park.name} from comparison`}
                            className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-zinc-300 transition-colors hover:text-white"
                          >
                            <XIcon size={15} weight="bold" />
                          </button>
                          <img
                            src={destinationCoverImage(park)}
                            alt=""
                            className="h-20 w-full rounded-xl object-cover lg:h-24"
                          />
                          <div className="mt-2 font-display text-base font-bold leading-tight text-white sm:text-lg">{park.name}</div>
                          <div className="mt-1.5 flex flex-col items-start gap-y-1 text-xs text-zinc-500 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3">
                            <span className="inline-flex items-center gap-1">
                              <MapPinIcon size={13} />
                              {[park.city, park.country].filter(Boolean).join(", ") || translateLabel("Location unavailable")}
                            </span>
                            {park.distance_km != null && (
                              <span className="font-semibold text-zinc-300">
                                {formatDistanceKm(park.distance_km, unitSystem)} away
                              </span>
                            )}
                            {compactPrice && (
                              <span className="rounded-full border border-[#CCFF00]/20 bg-[#CCFF00]/10 px-2 py-0.5 font-bold text-[#CCFF00]">
                                {compactPrice}
                              </span>
                            )}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Link
                              to={parkPath(park)}
                              className="inline-flex text-xs font-bold text-[#CCFF00] hover:text-[#B8E600]"
                            >
                              View park →
                            </Link>
                            <PlanThisRideButton
                              destination={park}
                              initialDate={selectedDate}
                              initialOrigin={effectiveOrigin}
                              source="compare_desktop"
                              compact
                            />
                          </div>
                        </div>
                      );
                    }

                    if (slot?.id) {
                      return (
                        <div key={slot.id} className="border-l border-white/[0.06] p-3">
                          <Skeleton className="h-24 w-full rounded-xl bg-white/5" />
                          <Skeleton className="mt-3 h-4 w-2/3 bg-white/5" />
                          <Skeleton className="mt-2 h-3 w-1/2 bg-white/5" />
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>

                {rows.map((row, index) => (
                  <div
                    key={translateLabel(row.label)}
                    className={`compare-grid grid ${index !== rows.length - 1 ? "border-b border-white/[0.06]" : ""}`}
                    style={columnStyle}
                  >
                    <div className="px-3 py-3 text-[11px] font-bold uppercase tracking-[0.13em] text-zinc-500 sm:px-4">
                      {translateLabel(row.label)}
                    </div>
                    {slots.map((slot) => (
                      slot?.item ? (
                        <CompareCell key={`${translateLabel(row.label)}-${slot.id}`}>
                          {row.render(slot.item)}
                        </CompareCell>
                      ) : (
                        <CompareCell key={`${translateLabel(row.label)}-${slot.id}`} muted>
                          Loading…
                        </CompareCell>
                      )
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <p className="mt-4 text-xs leading-5 text-zinc-600">
              Compare shows available Bikeparks Finder data. Opening status, prices, access rules and weather can change;
              check the destination's official information before travelling.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
